import React, { useState, useEffect } from 'react';
import { useWindowSize } from '../hooks/useWindowSize';
import { MasonryPhotoAlbum } from 'react-photo-album';
import 'react-photo-album/rows.css';
import 'react-photo-album/masonry.css';
import { authService } from '../utils/auth.ts';
import { formatDate } from '../utils/dateUtils';
import PhotoDetail from './PhotoDetail.tsx';
import CommentBadge from './CommentBadge';
import SkeletonGrid from './SkeletonGrid.tsx';

interface PhotoImageProps {
  photoId: string;
  filename: string;
  onClick: () => void;
  useThumbnail?: boolean;
  forceRefresh?: number; // キャッシュバスター用
  renderExtraLabels?: () => React.ReactNode; // 追加ラベル用
}

const PhotoImage: React.FC<PhotoImageProps> = React.memo(
  ({
    photoId,
    filename,
    onClick,
    useThumbnail = true,
    forceRefresh = 0,
    renderExtraLabels,
  }) => {
    const [imageUrl, setImageUrl] = useState<string>('');
    const [loading, setLoading] = useState(true);
    const [isThumbnail, setIsThumbnail] = useState(false);

    useEffect(() => {
      const loadImage = async () => {
        try {
          // サムネイルまたはフル画像のエンドポイントを選択
          const endpoint = useThumbnail
            ? `/photos/${photoId}/thumbnail`
            : `/photos/${photoId}/url`;

          // サムネイルリクエストはキャッシュを使用（3分間）
          const response = await authService.authFetch(endpoint, {
            useCache: useThumbnail,
            ttl: 3 * 60 * 1000, // 3分間キャッシュ
          });

          if (response.ok) {
            const data = await response.json();
            setImageUrl(data.url);
            setIsThumbnail(data.isThumbnail || false);
          }
        } catch (error) {
          console.error('Error loading image:', error);
        } finally {
          setLoading(false);
        }
      };
      loadImage();
    }, [photoId, useThumbnail, forceRefresh]);

    if (loading) {
      return (
        <div
          style={{
            width: '100%',
            height: '200px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'var(--accent-yellow)',
            opacity: 0.2,
          }}
        >
          <div className="spinner"></div>
        </div>
      );
    }

    if (!imageUrl) {
      return (
        <div
          style={{
            width: '100%',
            height: '200px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'var(--neutral-100)',
            borderRadius: 'var(--radius-lg)',
          }}
        >
          <div style={{ textAlign: 'center', color: 'var(--neutral-500)' }}>
            <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>📷</div>
            <div style={{ fontSize: '0.8rem' }}>画像を読み込めませんでした</div>
          </div>
        </div>
      );
    }

    return (
      <div style={{ position: 'relative' }}>
        <div className="photo-overlay"></div>
        <img
          src={imageUrl}
          alt={filename}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            cursor: 'pointer',
          }}
          onClick={onClick}
        />
        {/* ラベルコンテナ - flex縦並び */}
        <div
          style={{
            position: 'absolute',
            top: '8px',
            right: '8px',
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
            alignItems: 'flex-end',
          }}
        >
          {/* サムネイル読み込み中ラベル */}
          {useThumbnail && !isThumbnail && (
            <div
              style={{
                backgroundColor: 'var(--accent-yellow)',
                color: 'var(--neutral-50)',
                padding: '6px',
                borderRadius: 'var(--radius-md, 0.375rem)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: 'var(--shadow-sm)',
              }}
            >
              {/* Material Icons: photo_library */}
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="currentColor"
                style={{ opacity: 0.9 }}
              >
                <path d="M22,16V4c0-1.1-0.9-2-2-2H8c-1.1,0-2,0.9-2,2v12c0,1.1,0.9,2,2,2h12C21.1,18,22,17.1,22,16z M11.5,13L13,15l2.5-3l3.5,4.5 H9L11.5,13z M2,6v14c0,1.1,0.9,2,2,2h14v-2H4V6H2z" />
              </svg>
            </div>
          )}
          {/* 追加のラベル */}
          {renderExtraLabels && renderExtraLabels()}
        </div>
      </div>
    );
  }
);

interface Photo {
  PK: string;
  filename: string;
  s3_key: string;
  uploader_username: string;
  uploaded_at: string;
  file_size: number;
  width?: number;
  height?: number;
}

const Timeline: React.FC = () => {
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [hasMore, setHasMore] = useState(false);
  const [lastKey, setLastKey] = useState<string | null>(null);
  const [selectedPhotoId, setSelectedPhotoId] = useState<string | null>(null);
  const [thumbnailRefreshMap, setThumbnailRefreshMap] = useState<
    Record<string, number>
  >({});

  // 共通フックでwindowサイズを取得
  const { width: windowWidth } = useWindowSize();

  // 初回読み込み（reset=trueの場合は写真リストをクリア）
  const loadPhotos = async (reset = false) => {
    try {
      setIsLoading(true);
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8080';
      const url = new URL(`${apiUrl}/api/photos`);
      url.searchParams.set('limit', '20');

      // resetがfalseかつlastKeyがある場合はページネーション
      if (!reset && lastKey) {
        url.searchParams.set('lastKey', lastKey);
      }
      const response = await authService.authFetch(url.toString(), {
        useCache: reset, // 初回読み込み時のみキャッシュを使用
        ttl: 30 * 1000, // 30秒間キャッシュ（短めに設定）
      });
      if (!response.ok) {
        throw new Error('写真の取得に失敗しました');
      }

      const data = await response.json();
      const photosArray = data.photos || [];

      if (reset) {
        setPhotos(photosArray);
      } else {
        setPhotos(prev => [...prev, ...photosArray]);
      }

      setHasMore(data.hasMore);
      setLastKey(data.lastKey || null);
      setError('');
    } catch (err) {
      setError(err instanceof Error ? err.message : '写真の取得に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  // 初回読み込みのみ（依存関係なし）
  useEffect(() => {
    loadPhotos(true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const getPhotoId = (pk: string) => {
    // マルチテナント対応: TENANT#{tenantId}#PHOTO#{photoId} → {photoId}
    const match = pk.match(/PHOTO#(.+)$/);
    return match ? match[1] : pk;
  };

  // react-photo-album形式の写真データに変換
  const convertPhotosToAlbumFormat = (photos: Photo[]) => {
    return photos.map(photo => ({
      src: `/api/photos/${getPhotoId(photo.PK)}/thumbnail`, // サムネイル用エンドポイント
      width: photo.width || 800, // デフォルト寸法
      height: photo.height || 600,
      alt: photo.filename,
      // カスタムデータを追加
      originalPhoto: photo,
      photoId: getPhotoId(photo.PK),
    }));
  };

  const handlePhotoClick = (photoId: string) => {
    setSelectedPhotoId(photoId);
  };

  const handleCloseDetail = () => {
    setSelectedPhotoId(null);
  };

  const handlePhotoDeleted = (deletedPhotoId: string) => {
    // ローカルで即座に削除された写真を除去
    setPhotos(prevPhotos =>
      prevPhotos.filter(photo => getPhotoId(photo.PK) !== deletedPhotoId)
    );

    // モーダルも閉じる
    setSelectedPhotoId(null);

    // バックグラウンドでタイムラインを完全リフレッシュ
    setTimeout(() => {
      loadPhotos(true);
    }, 100);
  };

  const handleThumbnailUpdated = (photoId: string) => {
    // 特定のサムネイルのみ強制再読み込み
    setThumbnailRefreshMap(prev => ({
      ...prev,
      [photoId]: Date.now(), // タイムスタンプでキャッシュバスター
    }));

    // モーダルも閉じる
    setSelectedPhotoId(null);
  };

  const getCurrentPhotoIndex = () => {
    if (!selectedPhotoId) return -1;
    return photos.findIndex(photo => getPhotoId(photo.PK) === selectedPhotoId);
  };

  const handlePrevious = () => {
    const currentIndex = getCurrentPhotoIndex();
    if (currentIndex > 0) {
      const prevPhoto = photos[currentIndex - 1];
      setSelectedPhotoId(getPhotoId(prevPhoto.PK));
    }
  };

  const handleNext = () => {
    const currentIndex = getCurrentPhotoIndex();
    if (currentIndex < photos.length - 1) {
      const nextPhoto = photos[currentIndex + 1];
      setSelectedPhotoId(getPhotoId(nextPhoto.PK));
    }
  };

  if (error) {
    return (
      <div
        style={{
          padding: '1rem',
          textAlign: 'center',
          backgroundColor: 'var(--neutral-50)',
          margin: '1rem',
          borderRadius: 'var(--radius-lg)',
          color: 'var(--error)',
          border: '1px solid var(--error)',
        }}
      >
        {error}
        <br />
        <button
          onClick={() => loadPhotos(true)}
          style={{
            marginTop: '1rem',
            padding: '0.5rem 1rem',
            background:
              'var(--gradient-primary, linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%))',
            color: 'var(--neutral-50)',
            border: 'none',
            borderRadius: 'var(--radius-lg)',
            cursor: 'pointer',
          }}
        >
          再試行
        </button>
      </div>
    );
  }

  return (
    <div
      style={{
        padding: windowWidth > 480 ? '1rem' : '0.5rem',
        maxWidth: '1400px',
        margin: '0 auto',
        width: '100%',
      }}
    >
      {photos && photos.length === 0 && !isLoading && (
        <div className="empty-state fade-in">
          <div className="empty-state-icon">📷</div>
          <div className="empty-state-title">まだ写真がありません</div>
          <div className="empty-state-subtitle">
            最初の思い出をアップロードしてみましょう！
          </div>
        </div>
      )}

      {/* 初回ローディング時のスケルトン表示 */}
      {isLoading && photos.length === 0 && (
        <SkeletonGrid count={12} windowWidth={windowWidth} />
      )}

      <MasonryPhotoAlbum
        key={photos.map(photo => getPhotoId(photo.PK)).join(',')}
        photos={convertPhotosToAlbumFormat(photos)}
        columns={containerWidth => {
          if (containerWidth >= 1000) return 5;
          if (containerWidth >= 800) return 4;
          if (containerWidth >= 600) return 3;
          return 2;
        }}
        spacing={containerWidth => {
          if (containerWidth >= 768) return 12;
          if (containerWidth >= 480) return 8;
          return 4;
        }}
        padding={0}
        onClick={({ index }) => {
          const photo = photos[index];
          if (photo) {
            handlePhotoClick(getPhotoId(photo.PK));
          }
        }}
        render={{
          photo: ({ onClick }, { photo, width, height }) => {
            const photoId = (photo as any).photoId;
            const originalPhoto = (photo as any).originalPhoto;

            return (
              <div
                style={{
                  position: 'relative',
                  width,
                  height,
                  borderRadius: 'var(--radius-lg, 0.5rem)',
                  overflow: 'hidden',
                  backgroundColor: 'var(--neutral-50)',
                  boxShadow:
                    'var(--shadow-md, 0 4px 6px -1px rgb(0 0 0 / 0.1))',
                  cursor: 'pointer',
                  transition:
                    'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                }}
                onClick={onClick}
                className="photo-card fade-in photo-hover-effect"
                onMouseEnter={e => {
                  e.currentTarget.style.transform = 'scale(1.02)';
                  e.currentTarget.style.boxShadow =
                    'var(--shadow-lg, 0 10px 15px -3px rgb(0 0 0 / 0.1))';
                  const metadataCard = e.currentTarget.querySelector(
                    '.metadata-card'
                  ) as HTMLElement;
                  if (metadataCard) {
                    metadataCard.style.opacity = '1';
                    metadataCard.style.transform = 'translateY(0)';
                  }
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow =
                    'var(--shadow-md, 0 4px 6px -1px rgb(0 0 0 / 0.1))';
                  const metadataCard = e.currentTarget.querySelector(
                    '.metadata-card'
                  ) as HTMLElement;
                  if (metadataCard) {
                    metadataCard.style.opacity = '0';
                    metadataCard.style.transform = 'translateY(100%)';
                  }
                }}
              >
                <PhotoImage
                  photoId={photoId}
                  filename={originalPhoto.filename}
                  onClick={() => {}} // onClick は親で処理
                  useThumbnail={true}
                  forceRefresh={thumbnailRefreshMap[photoId] || 0}
                  renderExtraLabels={() => <CommentBadge photoId={photoId} />}
                />

                {/* ホバー時メタデータカード */}
                <div
                  style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    backgroundColor: 'rgba(248, 250, 252, 0.98)', // 少しグレーがかった白
                    backdropFilter: 'blur(6px)',
                    padding: windowWidth > 768 ? '0.75rem' : '0.5rem',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    fontSize: windowWidth > 768 ? '0.75rem' : '0.65rem',
                    opacity: '0',
                    transform: 'translateY(100%)',
                    transition:
                      'opacity 0.25s ease-out, transform 0.25s ease-out',
                    borderTop: '1px solid rgba(226, 232, 240, 0.8)', // ライトグレーボーダー
                    boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.1)', // 上向きの軽いシャドウ
                  }}
                  className="metadata-card"
                >
                  <span
                    style={{
                      fontWeight: '700',
                      color: 'var(--accent-mint-dark)', // エメラルドグリーン
                      letterSpacing: '0.025em',
                      maxWidth: '60%',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {originalPhoto.uploader_username}
                  </span>
                  <span
                    style={{
                      color: 'var(--text-dark)', // 読みやすいダークグレー
                      fontWeight: '500',
                    }}
                  >
                    {formatDate(originalPhoto.uploaded_at).split(' ')[0]}
                  </span>
                </div>
              </div>
            );
          },
        }}
      />

      {hasMore && (
        <div style={{ textAlign: 'center' }}>
          <button
            onClick={() => loadPhotos(false)}
            disabled={isLoading}
            className={isLoading ? 'button-loading' : ''}
            style={{
              margin: '1rem auto',
              padding: '0.75rem 1.5rem',
              background: isLoading
                ? 'var(--neutral-300, #cbd5e1)'
                : 'var(--gradient-primary, linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%))',
              color: 'var(--neutral-50)',
              border: 'none',
              borderRadius: 'var(--radius-lg)',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              fontSize: '1rem',
              fontWeight: '600',
              transition: 'all 0.2s ease-in-out',
              position: 'relative',
            }}
          >
            <span className="button-text">
              {isLoading ? '読み込み中...' : 'もっと見る'}
            </span>
          </button>
        </div>
      )}

      {/* 写真詳細モーダル */}
      {selectedPhotoId && (
        <PhotoDetail
          photoId={selectedPhotoId}
          onClose={handleCloseDetail}
          onPrevious={getCurrentPhotoIndex() > 0 ? handlePrevious : undefined}
          onNext={
            getCurrentPhotoIndex() < photos.length - 1 ? handleNext : undefined
          }
          hasPrevious={getCurrentPhotoIndex() > 0}
          hasNext={getCurrentPhotoIndex() < photos.length - 1}
          onPhotoDeleted={() => handlePhotoDeleted(selectedPhotoId)}
          onThumbnailUpdated={handleThumbnailUpdated}
        />
      )}
    </div>
  );
};

export default Timeline;
